import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { lightTheme } from './styles/theme';
import GlobalStyles from './styles/GlobalStyles';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { antiScrapingOptimized } from './utils/antiScrapingOptimized';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Section from './components/ui/Section';
import About from './components/sections/About';
import Projects from './components/sections/Projects';
import Tools from './components/sections/Tools';
import Resources from './components/sections/Resources';
import Contact from './components/sections/Contact';
import LegalNotice from './pages/LegalNotice';
import PrivacyPolicy from './pages/PrivacyPolicy';

/**
 * Main App component for Nadir's Portfolio
 * With GDPR-compliant routing and accessibility improvements
 */

const HomePage: React.FC = () => {
  const { t } = useLanguage();

  return (
    <>
      {/* Hero Section */}
      <Section
        id="hero"
        background="gradient"
        padding="large"
        as="section"
        aria-label={t('hero.title')}
      >
        <div style={{ textAlign: 'center', paddingTop: '100px' }}>
          <h1 style={{ fontSize: '4rem', marginBottom: '1rem' }}>
            {t('hero.title')}
          </h1>
          <p style={{ fontSize: '1.5rem', marginBottom: '2rem' }}>
            {t('hero.subtitle')}
          </p>
          <p style={{ fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto' }}>
            {t('hero.description')}
          </p>
        </div>
      </Section>

      {/* About Section */}
      <Section
        id="about"
        title={t('about.title')}
        subtitle={t('about.subtitle')}
        background="light"
        as="section"
        aria-labelledby="about-heading"
      >
        <About />
      </Section>

      {/* Projects Section */}
      <Section
        id="projects"
        title={t('projects.title')}
        subtitle={t('projects.subtitle')}
        background="dark"
        as="section"
        aria-labelledby="projects-heading"
      >
        <Projects />
      </Section>

      {/* Tools Section */}
      <Section
        id="tools"
        title={t('tools.title')}
        subtitle={t('tools.subtitle')}
        background="light"
        as="section"
        aria-labelledby="tools-heading"
      >
        <Tools />
      </Section>

      {/* Resources Section */}
      <Section
        id="resources"
        title={t('resources.title')}
        subtitle={t('resources.subtitle')}
        background="dark"
        as="section"
        aria-labelledby="resources-heading"
      >
        <Resources />
      </Section>

      {/* Contact Section */}
      <Section
        id="contact"
        title={t('contact.title')}
        subtitle={t('contact.subtitle')}
        background="light"
        as="section"
        aria-labelledby="contact-heading"
      >
        <Contact />
      </Section>
    </>
  );
};

const AppContent: React.FC = () => {
  useEffect(() => {
    // Initialize anti-scraping protection on startup
    antiScrapingOptimized.init();
    
    // Check for bots periodically
    const interval = setInterval(() => {
      const botDetection = antiScrapingOptimized.detectBot();
      if (botDetection.isBot) {
        console.warn('🚫 Bot detected:', botDetection);
      }
    }, 30000); // Check every 30 seconds

    return () => {
      clearInterval(interval);
      antiScrapingOptimized.cleanup();
    };
  }, []);

  return (
    <Router>
      <Header />
      <main id="main-content" role="main">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/mentions-legales" element={<LegalNotice />} />
          <Route path="/politique-confidentialite" element={<PrivacyPolicy />} />
        </Routes>
      </main>
      <Footer />
    </Router>
  );
};

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider theme={lightTheme}>
        <GlobalStyles theme={lightTheme} />
        <AppContent />
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
